import { useEffect, useState } from 'react';
import { Check, X, PencilLine } from 'lucide-react';
import { Button } from '@curtin-dxp/curtin-ui';
import { trpc } from '@/data-loaders';
import { useAwmsStore } from '@/store';
import { Note } from './Note';
import { UnitWorkloadDetails, UnitWorkloadCampus } from '#server/models';
import { IconComponent } from '@/components/Common/Icons';
import { Input, NumberInput } from '@/components/ui';
import { useHandleError, useHandleSuccess } from '@/hooks';
import { formatHours, allowOnlyIntegers, parseNumericValue } from '@/lib/utils';

interface TdTrdPanelProps {
  unitWorkloadId: number;
  unitWorkloadDetails: UnitWorkloadDetails;
  unitWorkloadCampuses: UnitWorkloadCampus[];
}
export const TdTrdPanel = ({ unitWorkloadId, unitWorkloadDetails, unitWorkloadCampuses }: TdTrdPanelProps) => {
  const handleError = useHandleError();
  const selectedCampusId = useAwmsStore((state) => state.selectedCampusId);
  const setSelectedCampusId = useAwmsStore((state) => state.setSelectedCampusId);
  const [selectedCampus, setSelectedCampus] = useState<UnitWorkloadCampus | null>(null);
  const [isPlannedStudentsEditable, setIsPlannedStudentsEditable] = useState(false);
  const setTaughtWeeksStore = useAwmsStore((state) => state.setTaughtWeeks);
  const setPlannedStudentsStore = useAwmsStore((state) => state.setPlannedStudents);
  const [plannedStudents, setPlannedStudents] = useState<string>('0');
  const trpcUtils = trpc.useUtils();

  const sendToastPlannedStudents = useHandleSuccess({
    title: 'Update unit workload planned students',
  });

  const handleInvalidateAndRefetch = async (unitWorkloadId: number) => {
    await trpcUtils.getUnitWorkloadCampuses.invalidate({ unitWorkloadId });
    await trpcUtils.getUnitWorkloadCampuses.refetch();

    await trpcUtils.trd.getUnitWorkloadTeachingHours.invalidate({ unitWorkloadId });
    await trpcUtils.trd.getUnitWorkloadTeachingHours.refetch();
  };

  const updateUWTPlannedStudentsMutation = trpc.updateUWTPlannedStudents.useMutation({
    onSuccess: async () => {
      await handleInvalidateAndRefetch(unitWorkloadId);
      sendToastPlannedStudents();
    },
    onError: (error) => {
      console.error('Error update planned students', error);
      handleError([error]);
    },
  });

  useEffect(() => {
    // set first campus as selectedCampusId if not selected yet
    if (unitWorkloadCampuses && unitWorkloadCampuses.length > 0 && unitWorkloadCampuses[0] && !selectedCampusId) {
      setSelectedCampusId(unitWorkloadCampuses[0].campusId);
    }
  }, [unitWorkloadCampuses, setSelectedCampusId]);

  useEffect(() => {
    if (selectedCampusId) {
      setSelectedCampus(unitWorkloadCampuses.find((campus) => campus.campusId === selectedCampusId) ?? null);
    }
  }, [selectedCampusId]);

  useEffect(() => {
    if (selectedCampus) {
      setPlannedStudents(selectedCampus.plannedStudents ?? '0');
      setTaughtWeeksStore(
        unitWorkloadCampuses.find((campus) => campus.campusId === selectedCampusId)?.taughtWeeks ?? 0,
      );
      setPlannedStudentsStore(Number(selectedCampus.plannedStudents ?? '0'));
    }
  }, [selectedCampus, selectedCampusId, setPlannedStudentsStore, setTaughtWeeksStore, unitWorkloadCampuses]);

  useEffect(() => {
    if (selectedCampus) {
      setPlannedStudents(selectedCampus?.plannedStudents ?? '0');
      setPlannedStudentsStore(Number(selectedCampus.plannedStudents ?? '0'));
    }
  }, [selectedCampus, setPlannedStudentsStore]);

  return (
    <div className="flex h-full shrink-0 flex-col items-start justify-between gap-5 bg-primary-website-background-colour pb-[56px] pt-5">
      <div className="flex w-full flex-col gap-5">
        <div className="px-5">
          <h4 className="font-proxima text-b1 font-bold">Select campus</h4>
        </div>
        <div className="flex flex-col">
          {unitWorkloadCampuses.length ? (
            unitWorkloadCampuses.map((unitWorkloadCampus) => (
              <a
                href="#"
                key={unitWorkloadCampus.campusId}
                onClick={() => setSelectedCampusId(unitWorkloadCampus.campusId)}
              >
                <div
                  className={`cursor-pointer p-2 px-5 text-b2 hover:bg-[#F1EFF6]
                    ${unitWorkloadCampus.campusId === selectedCampusId ? 'bg-[#E3DCFF]' : ''}`}
                >
                  {unitWorkloadCampus.campusName} {unitWorkloadCampus.campusStudyMode}
                </div>
              </a>
            ))
          ) : (
            <span className="px-5 text-b2 font-bold">Please select a campus from unit details screen.</span>
          )}
        </div>
        <div>
          <hr className="mx-auto w-[85%] bg-gray-300" />
        </div>
        <div className="flex flex-col gap-y-3 px-5 pt-3">
          <div className="grid grid-cols-[max-content_1fr] gap-x-5 gap-y-3 text-b2">
            <div>Previous year headcount</div>
            <div>{selectedCampus?.prevYearHeadcount}</div>
            <div>Max enrolment quota</div>
            <div>{selectedCampus?.maxEnrollmentQuota}</div>
            <div>Taught weeks</div>
            <div>{formatHours(Number(selectedCampus?.taughtWeeks), false, 0)}</div>
          </div>
          <div className="flex flex-row items-center gap-4">
            Planned students{' '}
            <NumberInput
              className="h-[32px] w-[117px] border-secondary-grey-03 text-b2"
              min={0}
              value={parseNumericValue(plannedStudents)}
              disabled={!isPlannedStudentsEditable}
              onChange={(e) => {
                setPlannedStudents(e.target.value);
                setPlannedStudentsStore(Number(e.target.value ?? '0'));
              }}
            />
            {isPlannedStudentsEditable ? (
              <div className="flex flex-row gap-1">
                <Button
                  size="icon"
                  variant="filled-secondary"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    setIsPlannedStudentsEditable(false);
                    if (selectedCampus) {
                      setPlannedStudents(selectedCampus.plannedStudents ?? '0');
                      setPlannedStudentsStore(Number(selectedCampus.plannedStudents ?? '0'));
                    }
                  }}
                >
                  <IconComponent IconType={X} />
                </Button>{' '}
                <Button
                  size="icon"
                  variant="filled-secondary"
                  className="h-8 w-8 p-0"
                  onClick={() => {
                    setIsPlannedStudentsEditable(false);
                    handleInvalidateAndRefetch(unitWorkloadId);
                    updateUWTPlannedStudentsMutation.mutate({
                      unitWorkloadId: unitWorkloadDetails.id,
                      campusId: selectedCampusId,
                      plannedStudents: plannedStudents === '' || plannedStudents == null ? '0' : plannedStudents,
                    });
                    setSelectedCampus({
                      plannedStudents: plannedStudents.toString(),
                      prevYearHeadcount: selectedCampus?.prevYearHeadcount ?? 0,
                      maxEnrollmentQuota: selectedCampus?.maxEnrollmentQuota ?? 0,
                      taughtWeeks: selectedCampus?.taughtWeeks ?? 0,
                      campusId: selectedCampus?.campusId ?? 0,
                      campusName: selectedCampus?.campusName ?? '',
                      campusStudyMode: selectedCampus?.campusStudyMode ?? '',
                    });
                    setPlannedStudentsStore(Number(plannedStudents ?? 0));
                  }}
                >
                  <IconComponent IconType={Check} />
                </Button>
              </div>
            ) : (
              <Button
                size="icon"
                variant="filled-secondary"
                className="h-8 w-8 p-0"
                onClick={() => {
                  setIsPlannedStudentsEditable(true);
                }}
              >
                <IconComponent IconType={PencilLine} />
              </Button>
            )}
          </div>
        </div>
      </div>
      <Note unitWorkloadDetails={unitWorkloadDetails} />
    </div>
  );
};
